import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { adminCategoryAPI } from '../services/api';
import CategoryModal from '../components/CategoryModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/Categories.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const fetchCategories = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await adminCategoryAPI.getAll(params);
      if (response.data.success) {
        setCategories(response.data.data.categories);
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      setError('Failed to load categories');
      console.error('Fetch categories error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setModalMode('create');
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setModalMode('edit');
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleCategorySaved = () => {
    setShowCategoryModal(false);
    fetchCategories();
  };

  const handleCategoryDeleted = () => {
    setShowDeleteModal(false);
    fetchCategories();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="categories-page">
      <div className="page-header">
        <div>
          <h1>Product Categories</h1>
          <p>Manage product categories for your store</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateCategory}>
          <FaPlus /> Add Category
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {loading ? (
        <div className="loading">Loading categories...</div>
      ) : (
        <>
          <div className="categories-grid">
            {categories.length > 0 ? (
              categories.map((category) => (
                <div key={category._id} className="category-card">
                  <div className="category-header" style={{ backgroundColor: category.color || '#667eea' }}>
                    {category.imageUrl ? (
                      <img src={category.imageUrl} alt={category.title} className="category-image" />
                    ) : (
                      <div className="category-placeholder">
                        <span>{category.title.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="category-body">
                    <h3>{category.title}</h3>
                    <p>{category.description || 'No description'}</p>
                    <div className="category-meta">
                      {/* <span className="color-badge" style={{ backgroundColor: category.color }}>
                        {category.color}
                      </span> */}
                      {/* <span className="date">
                        {new Date(category.createdAt).toLocaleDateString()}
                      </span> */}
                    </div>
                  </div>
                  <div className="category-actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEditCategory(category)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteCategory(category)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">No categories found</div>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={!pagination.hasPrevPage}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>

              <span className="pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
                ({pagination.totalCategories} total categories)
              </span>

              <button
                className="btn btn-secondary"
                disabled={!pagination.hasNextPage}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showCategoryModal && (
        <CategoryModal
          mode={modalMode}
          category={selectedCategory}
          onClose={() => setShowCategoryModal(false)}
          onSaved={handleCategorySaved}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          item={selectedCategory}
          itemType="category"
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleCategoryDeleted}
          deleteFunction={adminCategoryAPI.delete}
        />
      )}
    </div>
  );
};

export default Categories;
